import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Stack,
  alpha,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import InfoIcon from '@mui/icons-material/Info';
import { callerIdService } from '../services/callerIdService';
import { CallerIdVerification } from '../types';

const steps = [
  'How It Works',
  'Enter Number',
  'Verify',
  "You're All Set!",
];

// Simple US phone number formatter
const formatPhoneInput = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  // Remove leading 1 for display
  const local = digits.startsWith('1') && digits.length > 10 ? digits.slice(1) : digits;
  if (local.length <= 3) return local;
  if (local.length <= 6) return `(${local.slice(0, 3)}) ${local.slice(3)}`;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6, 10)}`;
};

const toE164 = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (digits.startsWith('1') && digits.length === 11) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
};

const formatForDisplay = (e164: string): string => {
  const digits = e164.replace(/\D/g, '');
  const local = digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length === 10) {
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  }
  return e164;
};

const CallerIdSetup: React.FC = () => {
  // Wizard state
  const [showWizard, setShowWizard] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [phoneInput, setPhoneInput] = useState('');
  const [friendlyName, setFriendlyName] = useState('');
  const [validationCode, setValidationCode] = useState('');
  const [callInitiated, setCallInitiated] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // List state
  const [verifiedNumbers, setVerifiedNumbers] = useState<CallerIdVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; phoneNumber: string; friendlyName: string }>({
    open: false,
    phoneNumber: '',
    friendlyName: '',
  });

  // Polling ref
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartRef = useRef<number>(0);

  const loadVerifiedNumbers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await callerIdService.listVerified();
      setVerifiedNumbers(response.data.results?.verified_numbers || []);
    } catch (err) {
      console.error('Failed to load verified numbers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVerifiedNumbers();
  }, [loadVerifiedNumbers]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const phoneDigits = phoneInput.replace(/\D/g, '');
  const isValidPhone = phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits.startsWith('1'));

  const handleInitiateVerification = async () => {
    setError(null);
    setVerifying(true);
    try {
      const e164 = toE164(phoneInput);

      // Check if already in verified list
      if (verifiedNumbers.some((n) => n.phone_number === e164)) {
        setError('This number is already verified.');
        setVerifying(false);
        return;
      }

      const response = await callerIdService.initiateVerification(e164, friendlyName);
      setValidationCode(response.data.results?.validation_code || '');
      setCallInitiated(true);

      // Start polling for verification status
      pollingStartRef.current = Date.now();
      pollingRef.current = setInterval(async () => {
        // Stop after 90 seconds
        if (Date.now() - pollingStartRef.current > 90000) {
          if (pollingRef.current) clearInterval(pollingRef.current);
          return;
        }

        try {
          const statusResp = await callerIdService.checkStatus(e164);
          if (statusResp.data.results?.verified) {
            if (pollingRef.current) clearInterval(pollingRef.current);
            setVerified(true);
            setActiveStep(3);
          }
        } catch {
          // Ignore polling errors
        }
      }, 3000);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to initiate verification');
    } finally {
      setVerifying(false);
    }
  };

  const handleRetry = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    setCallInitiated(false);
    setValidationCode('');
    setVerified(false);
    setError(null);
  };

  const handleDone = () => {
    setShowWizard(false);
    setActiveStep(0);
    setPhoneInput('');
    setFriendlyName('');
    setValidationCode('');
    setCallInitiated(false);
    setVerified(false);
    setError(null);
    loadVerifiedNumbers();
  };

  const handleDelete = async () => {
    try {
      await callerIdService.removeVerified(deleteDialog.phoneNumber);
      setSnackbar({ open: true, message: 'Number removed successfully', severity: 'success' });
      setDeleteDialog({ open: false, phoneNumber: '', friendlyName: '' });
      loadVerifiedNumbers();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Failed to remove number',
        severity: 'error',
      });
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Show Your Business Number on Calls
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              When we call your patients, they'll see your real business number instead of an
              unknown number. This makes patients more likely to answer.
            </Typography>
            <Box sx={{ pl: 2, mb: 3 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Typography sx={{ fontWeight: 600, minWidth: 24 }}>1.</Typography>
                  <Typography>
                    Enter your business phone number below.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Typography sx={{ fontWeight: 600, minWidth: 24 }}>2.</Typography>
                  <Typography>
                    We'll call that number and ask someone at your office to enter a 6-digit code on
                    the phone keypad.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Typography sx={{ fontWeight: 600, minWidth: 24 }}>3.</Typography>
                  <Typography>
                    Once verified, all outbound calls to patients will show your business number.
                  </Typography>
                </Box>
              </Stack>
            </Box>
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
              This only changes what patients see for <strong>phone calls</strong>. Text messages
              will continue to come from your Yobi number.
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" onClick={() => setActiveStep(1)}>
                Get Started
              </Button>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Enter Your Business Phone Number
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Enter the phone number you'd like patients to see when they receive calls.
            </Typography>
            <Stack spacing={3} sx={{ maxWidth: 400 }}>
              <TextField
                fullWidth
                label="Business Phone Number"
                value={formatPhoneInput(phoneInput)}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  setPhoneInput(digits.slice(0, 11));
                }}
                placeholder="(555) 867-5309"
                helperText={phoneInput && !isValidPhone ? 'Enter a valid 10-digit US phone number' : ''}
                error={!!phoneInput && !isValidPhone}
              />
              <TextField
                fullWidth
                label="Friendly Name (optional)"
                value={friendlyName}
                onChange={(e) => setFriendlyName(e.target.value)}
                placeholder="Main Office"
                helperText="A label to help you identify this number"
              />
            </Stack>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button onClick={() => { setActiveStep(0); setError(null); }}>Back</Button>
              <Button
                variant="contained"
                onClick={() => { setError(null); setActiveStep(2); }}
                disabled={!isValidPhone}
              >
                Next
              </Button>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Verify Your Number
            </Typography>

            {!callInitiated ? (
              <>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  We're about to call <strong>{formatForDisplay(toE164(phoneInput))}</strong>. Make
                  sure someone at your office is ready to answer.
                </Alert>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleInitiateVerification}
                    disabled={verifying}
                    startIcon={verifying ? <CircularProgress size={20} /> : undefined}
                  >
                    {verifying ? 'Initiating Call...' : 'Call Now'}
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  We're calling now! When someone answers, they'll hear a message asking them to
                  enter this code on the phone keypad:
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1.5,
                    my: 4,
                  }}
                >
                  {validationCode.split('').map((digit, i) => (
                    <Paper
                      key={i}
                      sx={{
                        width: 64,
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '2.5rem',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        bgcolor: alpha('#1e293b', 0.04),
                        border: '2px solid',
                        borderColor: alpha('#1e293b', 0.15),
                      }}
                    >
                      {digit}
                    </Paper>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Waiting for verification...
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button onClick={handleRetry} variant="outlined" size="small">
                    Didn't work? Try Again
                  </Button>
                </Box>
              </>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
              <Button onClick={() => { handleRetry(); setActiveStep(1); }}>
                Back
              </Button>
            </Box>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: '#22c55e', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              You're All Set!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your number is now verified! When we call patients, they'll see{' '}
              <strong>{formatForDisplay(toE164(phoneInput))}</strong> as the caller.
            </Typography>
            <Button variant="contained" onClick={handleDone} size="large">
              Done
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (showWizard) {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto' }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderStepContent()}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PhoneForwardedIcon sx={{ fontSize: 28, color: '#1e293b' }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Caller ID
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowWizard(true)}
        >
          Verify a New Number
        </Button>
      </Box>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        Verified numbers can be used as caller ID for <strong>outbound calls</strong>. Text messages
        are not affected.
      </Alert>

      {verifiedNumbers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PhoneForwardedIcon sx={{ fontSize: 48, color: alpha('#000', 0.2), mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Verified Numbers Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Verify your business phone number so patients see it when they receive calls.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowWizard(true)}>
            Verify a Number
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Phone Number</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Verified</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {verifiedNumbers.map((num) => (
                <TableRow key={num.phone_number}>
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatForDisplay(num.phone_number)}
                    </Typography>
                  </TableCell>
                  <TableCell>{num.friendly_name || '-'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label="Verified"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {num.verified_at
                          ? new Date(num.verified_at).toLocaleDateString()
                          : ''}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        setDeleteDialog({
                          open: true,
                          phoneNumber: num.phone_number,
                          friendlyName: num.friendly_name,
                        })
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, phoneNumber: '', friendlyName: '' })}>
        <DialogTitle>Remove Verified Number</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove{' '}
            <strong>
              {formatForDisplay(deleteDialog.phoneNumber)}
              {deleteDialog.friendlyName ? ` (${deleteDialog.friendlyName})` : ''}
            </strong>
            ? Outbound calls will no longer show this number as the caller ID.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, phoneNumber: '', friendlyName: '' })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CallerIdSetup;
